import { useEffect, useState } from 'react'
import { DollarSign } from 'lucide-react'
import Modal from '../../../components/ERP/Modal/Modal'
import ErpPeriodDropdown from '../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import { CalendarDatePicker } from '../../../components/ERP/Calendar/Calendar'
import { patientService } from '../../../services/patientApi'
import { financialAccountService } from '../../../services/erpApi'
import { todayInput } from './patientFormat'
import './PatientsPage.css'

function accountLabel(account) {
  const kind = account.account_type === 'digital' ? 'Digital' : account.account_type === 'physical' ? 'Física' : ''
  const code = account.code ? `${account.code} — ` : ''
  return `${code}${account.name}${kind ? ` (${kind})` : ''}`
}

function categoryLabel(category) {
  return category.parentName ? `${category.parentName} · ${category.name}` : category.name
}

function FieldDropdown({ value, options, onChange, searchable = false, ariaLabel }) {
  return (
    <ErpPeriodDropdown
      className="patients-field-dropdown"
      label=""
      align="left"
      portal
      searchable={searchable}
      ariaLabel={ariaLabel}
      value={value}
      options={options}
      onChange={onChange}
    />
  )
}

function AddPaymentModal({ isOpen, patientId, patientName, onClose, onAdded }) {
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [categories, setCategories] = useState([])
  const [amount, setAmount] = useState('')
  const [financialAccountId, setFinancialAccountId] = useState('')
  const [accounts, setAccounts] = useState([])
  const [accountsError, setAccountsError] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [dueDate, setDueDate] = useState(todayInput())
  const [monthlyInstallments, setMonthlyInstallments] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined
    let active = true
    setAccountsError('')
    Promise.all([
      financialAccountService.getAll({ active: true, limit: 200 }),
      patientService.financialCategories()
    ])
      .then(([accountsResponse, categoriesResponse]) => {
        if (!active) return
        const list = Array.isArray(accountsResponse?.data) ? accountsResponse.data : []
        const payload = categoriesResponse?.data || {}
        const categoryList = Array.isArray(payload.categories) ? payload.categories : []
        const defaultId = payload.defaultCategoryId ? String(payload.defaultCategoryId) : ''
        setAccounts(list)
        setCategories(categoryList)
        setFinancialAccountId((current) => (
          list.some((item) => String(item.id) === current) ? current : (list[0] ? String(list[0].id) : '')
        ))
        setCategory((current) => (
          categoryList.some((item) => String(item.id) === current) ? current : defaultId
        ))
      })
      .catch(() => {
        if (active) setAccountsError('Não foi possível carregar categorias e contas do financeiro')
      })
    return () => {
      active = false
    }
  }, [isOpen])

  const submit = async (event) => {
    event.preventDefault()
    if (!category) {
      setError('Selecione a categoria financeira')
      return
    }
    setSaving(true)
    setError('')
    try {
      await patientService.addCharge(patientId, {
        description,
        financialCategoryId: category,
        amount,
        financialAccountId,
        invoiceNumber,
        dueDate,
        monthlyInstallments
      })
      onAdded()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível lançar a cobrança')
    } finally {
      setSaving(false)
    }
  }

  const categoryOptions = categories.map((item) => ({
    value: String(item.id),
    label: categoryLabel(item)
  }))
  const accountOptions = accounts.map((account) => ({
    value: String(account.id),
    label: accountLabel(account)
  }))

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Novo lançamento financeiro"
      subtitle="Faturamento e contas hospitalares"
      icon={<DollarSign size={22} />}
      size="medium"
      footer={(
        <button type="submit" form="payment-form" disabled={saving || accounts.length === 0 || !category}>
          {saving ? 'Salvando...' : 'Lançar no prontuário'}
        </button>
      )}
    >
      <form id="payment-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        {accountsError && <p className="patients-alert-strip">{accountsError}</p>}
        <p className="patients-note">Paciente: <strong>{patientName}</strong></p>
        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-span-2">
            <label>Descrição do serviço / fatura *</label>
            <input required value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Diária de UTI + oxigenoterapia" />
          </div>
          <div>
            <label>Categoria</label>
            <FieldDropdown
              value={category}
              options={categoryOptions}
              onChange={setCategory}
              searchable
              ariaLabel="Categoria"
            />
          </div>
          <div>
            <label>{monthlyInstallments ? 'Valor de cada parcela (R$) *' : 'Valor total (R$) *'}</label>
            <input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="patients-span-2">
            <label>Conta do extrato de caixa *</label>
            <FieldDropdown
              value={financialAccountId}
              options={accountOptions}
              onChange={setFinancialAccountId}
              searchable
              ariaLabel="Conta do extrato de caixa"
            />
            {accounts.length === 0 && !accountsError && (
              <p className="patients-muted">Nenhuma conta ativa no extrato de caixa. Cadastre uma conta antes de lançar.</p>
            )}
          </div>
          <div>
            <label>Nº da fatura</label>
            <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </div>
          <div className="patients-evolution-date">
            <label>{monthlyInstallments ? 'Vencimento da 1ª parcela' : 'Vencimento'}</label>
            <CalendarDatePicker
              value={dueDate}
              onChange={setDueDate}
              title="Vencimento"
              placeholder="Selecionar data"
            />
          </div>
          <label className="patients-check-label patients-span-2">
            <input type="checkbox" checked={monthlyInstallments} onChange={(e) => setMonthlyInstallments(e.target.checked)} />
            Lançar 12 parcelas mensais
          </label>
          {monthlyInstallments && (
            <p className="patients-note patients-span-2">
              Serão criados 12 títulos, um por mês, a partir de {dueDate.split('-').reverse().join('/')}, na conta selecionada.
              {amount ? ` Total: ${(Number(amount) * 12).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.` : ''}
            </p>
          )}
        </div>
      </form>
    </Modal>
  )
}

export default AddPaymentModal
